// Currency helpers: every property price is stored in SGD and displayed to clients in CHF.
import { useEffect, useState } from "react";

// Fallback used until (or if) the live rate cannot be fetched. ECB reference, Oct 2026.
export const FALLBACK_SGD_TO_CHF = 0.646;

const CACHE_KEY = "asp_sgd_chf_rate_v1";
const CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours

let currentRate = FALLBACK_SGD_TO_CHF;
let inflight = null;
const listeners = new Set();

function readCache() {
  try {
    const c = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
    if (c && c.rate > 0 && Date.now() - c.at < CACHE_TTL_MS) return c.rate;
  } catch (_) { /* ignore */ }
  return null;
}

const cached = typeof window !== "undefined" ? readCache() : null;
if (cached) currentRate = cached;

async function fetchRate() {
  const sources = [
    async () => (await (await fetch("https://api.frankfurter.dev/v1/latest?base=SGD&symbols=CHF")).json()).rates.CHF,
    async () => (await (await fetch("https://open.er-api.com/v6/latest/SGD")).json()).rates.CHF,
  ];
  for (const src of sources) {
    try {
      const r = Number(await src());
      if (r > 0.3 && r < 1.2) return r; // sanity bounds
    } catch (_) { /* try next source */ }
  }
  return null;
}

function ensureRate() {
  if (cached || inflight) return;
  inflight = fetchRate().then((r) => {
    if (!r) return;
    currentRate = r;
    try { localStorage.setItem(CACHE_KEY, JSON.stringify({ rate: r, at: Date.now() })); } catch (_) { /* ignore */ }
    listeners.forEach((fn) => fn(r));
  });
}

/** Live SGD→CHF rate (re-renders once the live rate arrives). */
export function useSgdChfRate() {
  const [rate, setRate] = useState(currentRate);
  useEffect(() => {
    listeners.add(setRate);
    ensureRate();
    setRate(currentRate);
    return () => listeners.delete(setRate);
  }, []);
  return rate;
}

const nf0 = new Intl.NumberFormat("de-CH", { maximumFractionDigits: 0 });

/** Format a CHF amount. compact: CHF 1.2M / CHF 850K */
export function formatCHF(chf, { compact = false } = {}) {
  if (chf == null || Number.isNaN(chf)) return "";
  if (compact) {
    const abs = Math.abs(chf);
    if (abs >= 1e9) return `CHF ${+(chf / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `CHF ${+(chf / 1e6).toFixed(abs >= 1e7 ? 1 : 2)}M`;
    if (abs >= 1e3) return `CHF ${Math.round(chf / 1e3)}K`;
  }
  return `CHF ${nf0.format(Math.round(chf))}`;
}

/** Convert an SGD number to a formatted CHF string. */
export function sgdToChf(sgd, rate, opts) {
  if (sgd == null || sgd === "" || Number.isNaN(Number(sgd))) return "";
  return formatCHF(Number(sgd) * rate, opts);
}

const MULT = { K: 1e3, M: 1e6, B: 1e9, k: 1e3, m: 1e6, b: 1e9 };

/**
 * Rewrite every SGD amount inside a display string as CHF, keeping surrounding text.
 *   "From $1,876,500"       -> "From CHF 1’212’219"
 *   "($2,781 PSF)"          -> "(CHF 1’797 PSF)"
 *   "S$1.16 Billion in 24h" -> "CHF 749M in 24h"
 * Strings without an amount ("Price on Request", "Sold Out") are returned unchanged.
 */
export function convertSgdText(text, rate) {
  if (text == null) return text;
  const s = String(text);
  return s.replace(
    /(?:S\$|SGD\s?|\$)\s?(\d[\d,]*(?:\.\d+)?)\s?(Billion|Million|bn|[KMBkmb])?\b/g,
    (_, num, unit) => {
      let v = parseFloat(num.replace(/,/g, ""));
      let compact = false;
      if (unit) {
        const u = /^billion|bn$/i.test(unit) ? "B" : /^million$/i.test(unit) ? "M" : unit;
        v *= MULT[u] || 1;
        compact = true;
      }
      return formatCHF(v * rate, { compact });
    }
  );
}

/** "From $1,876,500" | "$1,876,500" | "N/A" | "" -> "From CHF 1’212’219" | "Price on Request" (sold-out labels kept). */
export function formatPriceFrom(priceFrom, rate) {
  const p = String(priceFrom || "").trim();
  if (!p || /^n\/?a$/i.test(p) || /price on request/i.test(p)) return "Price on Request";
  if (/sold\s*out/i.test(p)) return "Sold Out";
  if (!/\d/.test(p)) return p;
  const converted = convertSgdText(p, rate);
  return /^from\b/i.test(converted) ? converted : `From ${converted}`;
}

/** "($2,781 PSF)" | "$2,781 psf" -> "(CHF 1’797 PSF)" */
export function formatPsf(psf, rate) {
  const p = String(psf || "").trim();
  if (!p) return "";
  const converted = convertSgdText(p, rate);
  return converted.startsWith("(") ? converted : `(${converted})`;
}
